import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from '../Motion';
import styles from './TurnStatus.module.css';
import { formatDuration, turnStatus, type TurnClock } from './turn-status';
import { useNow } from './useTurnClock';

interface TurnStatusLineProps {
  readonly clock: TurnClock;
}

/** Speaks up only when a streaming turn has gone quiet: the server has sent
 *  nothing for a while, but the connection is still open. While events keep
 *  arriving the turn shows its own progress (thinking ticker, streaming
 *  text), so this renders nothing. A closed connection renders
 *  `TurnInterruptedNotice` instead. */
export function TurnStatusLine({ clock }: TurnStatusLineProps) {
  const status = turnStatus(clock, useNow());
  if (status.kind !== 'quiet') {
    return null;
  }

  return (
    <div
      className={`${styles.quiet} ${motion.fadeIn}`}
      data-testid="turn-status"
      role="status"
    >
      <span className={styles.quietDot} aria-hidden />
      <span>
        Still working · no update for {formatDuration(status.quietMs)}. Some
        steps, like generating a file, take a few minutes.
      </span>
    </div>
  );
}

/** Shown on a reply whose stream closed before the server finished it. */
export function TurnInterruptedNotice() {
  return (
    <div className={styles.interrupted} role="status">
      <FontAwesomeIcon icon={faTriangleExclamation} />
      <span>
        This response stopped before it finished. Send your message again to
        retry.
      </span>
    </div>
  );
}
